import { ReportGroup } from '@/api/generatedApi';
import { Box } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ListChildComponentProps, ListOnItemsRenderedProps, ListOnScrollProps, VariableSizeList } from 'react-window';
import { useReportMasterDetail } from '../ReportMasterDetailContext';
import { ReportSample } from './ReportSample';
import { GroupSampleHeight } from './ReportSample.styles';

const OVERSCAN_COUNT = 3;
const EXPANDED_HEIGHT_ESTIMATE = 320;
const PREFETCH_THRESHOLD = 5;

interface GroupRowData {
  groups: ReportGroup[];
  selectedGroupId: string | undefined;
  expandedIndex: number;
  measureExpandedRow: (node: HTMLDivElement | null) => void;
}

const GroupRow = ({ index, style, data }: ListChildComponentProps<GroupRowData>) => {
  const { groups, selectedGroupId, expandedIndex, measureExpandedRow } = data;
  const group = groups[index];

  return (
    <div style={style}>
      <div ref={index === expandedIndex ? measureExpandedRow : undefined}>
        <ReportSample
          group={group}
          groupIndex={index + 1}
          isSelected={selectedGroupId !== undefined && group.id === selectedGroupId}
          isLastItem={index === groups.length - 1}
        />
      </div>
    </div>
  );
};

const useMeasuredExpandedHeight = () => {
  const [height, setHeight] = useState(EXPANDED_HEIGHT_ESTIMATE);
  const observerReference = useRef<ResizeObserver>(null);

  const measureExpandedRow = useCallback((node: HTMLDivElement | null) => {
    observerReference.current?.disconnect();
    if (!node) return;
    const observer = new ResizeObserver(entries => {
      const measured = entries[0]?.contentRect.height;
      if (measured) setHeight(measured);
    });
    observer.observe(node);
    observerReference.current = observer;
  }, []);

  useEffect(() => () => observerReference.current?.disconnect(), []);

  return { expandedHeight: height, measureExpandedRow };
};

export const ReportGroupsList = ({
  groups,
  selectedGroupId,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  groups: ReportGroup[];
  selectedGroupId: string | undefined;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void | Promise<unknown>;
}) => {
  const { hasMatchData } = useReportMasterDetail();
  const listReference = useRef<VariableSizeList>(null);
  const hasScrolledToSelected = useRef(false);
  const hasUserScrolled = useRef(false);
  const { expandedHeight, measureExpandedRow } = useMeasuredExpandedHeight();

  const selectedIndex = groups.findIndex(group => group.id === selectedGroupId);
  const expandedIndex = hasMatchData ? selectedIndex : -1;

  const getItemSize = useCallback(
    (index: number) => (index === expandedIndex ? expandedHeight : GroupSampleHeight),
    [expandedIndex, expandedHeight]
  );

  useEffect(() => {
    listReference.current?.resetAfterIndex(0);
  }, [getItemSize]);

  useEffect(() => {
    if (hasScrolledToSelected.current || hasUserScrolled.current || selectedIndex === -1) return;
    hasScrolledToSelected.current = true;
    listReference.current?.scrollToItem(selectedIndex, 'start');
  }, [selectedIndex]);

  const handleScroll = useCallback(({ scrollOffset, scrollUpdateWasRequested }: ListOnScrollProps) => {
    if (!scrollUpdateWasRequested && scrollOffset > 0) hasUserScrolled.current = true;
  }, []);

  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }: ListOnItemsRenderedProps) => {
      if (isFetchingNextPage || !hasNextPage) return;
      if (visibleStopIndex >= groups.length - PREFETCH_THRESHOLD) fetchNextPage();
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage, groups.length]
  );

  return (
    <Box sx={{ height: '100%' }}>
      <AutoSizer>
        {({ height, width }) => (
          <VariableSizeList
            ref={listReference}
            height={height}
            width={width}
            itemCount={groups.length}
            itemSize={getItemSize}
            itemKey={index => groups[index].id}
            itemData={{ groups, selectedGroupId, expandedIndex, measureExpandedRow }}
            overscanCount={OVERSCAN_COUNT}
            onItemsRendered={handleItemsRendered}
            onScroll={handleScroll}
          >
            {GroupRow}
          </VariableSizeList>
        )}
      </AutoSizer>
    </Box>
  );
};
